import { useState, useEffect } from 'react';

import { Settings, UserPlus, Plus, Check, ChevronDown, Zap, Sparkles, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAppNavigate } from '@/hooks/useAppNavigate';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatures } from '@/hooks/useFeatures';
import { useByocEnabled } from '@/hooks/use-byoc-enabled';
import { useAuth } from '@/contexts/AuthContext';
import { creditsApi, type CreditDetailsResponse } from '@/lib/api/credits';
import { byocApi, type ByocStatus } from '@/lib/api/byoc';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Hint } from '@/components/ui/hint';
import { CreateWorkspaceDialog } from '@/components/workspace-members/create-workspace-dialog';
import { InviteMemberDialog } from '@/components/workspace-members/invite-member-dialog';

import { useWorkspace } from '@/contexts/workspace-context';
import { useEmbedStore } from '@/stores/embed-store';
import { cn } from '@/lib/utils';

interface WorkspaceSelectorProps {
  collapsed?: boolean;
  /** Returns true if navigation was blocked (unsaved changes). */
  onNavGuard?: (href: string) => boolean;
}

/**
 * Lovable-style workspace selector dropdown
 * Shows current workspace with expandable menu for switching/managing workspaces
 */
export function WorkspaceSelector({ collapsed = false, onNavGuard }: WorkspaceSelectorProps) {
  const { user, getIdToken } = useAuth();
  const appNavigate = useAppNavigate();
  const { canInviteMembers, canCreateWorkspace } = usePermissions();
  const { features } = useFeatures();
  const { enabled: byocEnabled } = useByocEnabled();
  const [open, setOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [creditDetails, setCreditDetails] = useState<CreditDetailsResponse | null>(null);
  const [byocStatus, setByocStatus] = useState<ByocStatus | null>(null);
  const { activeWorkspace, workspaces, currentWorkspace, refreshWorkspaces, activeWorkspaceId } =
    useWorkspace();
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);

  // Fetch credit details and BYOC status (BYOC fetch skipped when feature disabled)
  const fetchWorkspaceData = async () => {
    if (!user || !activeWorkspaceId) return;
    try {
      const token = await getIdToken();
      if (token) {
        const [credits, byoc] = await Promise.all([
          creditsApi.getDetails(token),
          byocEnabled
            ? byocApi.getStatus(token).catch(
                () =>
                  ({ connected: false, maskedKey: null, connectedAt: null }) as ByocStatus,
              )
            : Promise.resolve<ByocStatus>({
                connected: false,
                maskedKey: null,
                connectedAt: null,
              }),
        ]);
        setCreditDetails(credits);
        setByocStatus(byoc);
      }
    } catch (error) {
      console.error('Failed to fetch workspace data:', error);
    }
  };

  // Fetch on workspace change OR once `byocEnabled` resolves for an internal user
  useEffect(() => {
    fetchWorkspaceData();
    // `byocEnabled` starts false while useInternalUser loads; when it flips true
    // (internal user on a flag-off env) we need to re-fetch to pick up real status.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeWorkspaceId, getIdToken, byocEnabled]);

  // Refresh when popover opens
  useEffect(() => {
    if (open) {
      fetchWorkspaceData();
    }
  }, [open]);

  // Format BYOC connection message based on auth method
  const getByocMessage = (method?: string) => {
    if (method?.includes('oauth')) return 'Using your Claude subscription';
    return 'Using your Claude API key';
  };

  // Matches CreditDrawer.tsx's formatResetDate exactly, for copy consistency between the two
  // credit surfaces the user sees (workspace switcher + agent panel drawer).
  const formatResetDate = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'next month';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // User's default workspace (no ?ws needed in URL)
  const defaultWorkspaceId = user?.workspaceId;

  // For platform admin viewing non-member workspace, use currentWorkspace
  // Otherwise use activeWorkspace from member list
  const displayWorkspace = currentWorkspace
    ? {
        id: currentWorkspace.id,
        name: currentWorkspace.name,
        memberCount: 0, // Unknown for non-member workspace
      }
    : activeWorkspace;

  if (!displayWorkspace) {
    return null;
  }

  // Get workspace initial for avatar
  const getInitial = (name: string) => name.charAt(0).toUpperCase();

  // Calculate credit usage percentage (for progress bar).
  // Fill is subscriptionBalance / allocated — NOT total balance — because top-up credits
  // aren't part of the plan allocation and would overflow the denominator. Guard allocated=0
  // (free-daily before the UI cap applies) to avoid NaN/Infinity, and floor at 0 so a negative
  // subscription balance never renders a negative-width bar.
  const creditPercentage = creditDetails && creditDetails.allocated > 0
    ? Math.min(100, Math.max(0, Math.round((creditDetails.subscriptionBalance / creditDetails.allocated) * 100)))
    : 0;

  // Total balance (subscription + top-up) is what actually gates agent work — a negative
  // balance is a legitimate overage state (credits deduct after a run completes), not a bug,
  // so it gets its own distinct warning treatment rather than a misleadingly "full" bar.
  const isOutOfCredits = creditDetails ? creditDetails.balance <= 0 : false;

  // Free-tier daily-credit model: allocated=50 there is a UI cap, not a real plan allocation.
  const isFreeDaily = creditDetails?.freeDaily?.isFreeDaily ?? false;

  // Navigate to billing page
  const handleBillingClick = () => {
    if (onNavGuard?.('/dashboard/settings/billing')) {
      setOpen(false);
      return;
    }
    appNavigate('/dashboard/settings/billing');
    setOpen(false);
  };

  // Handle workspace switch - reload page to clear all state
  const handleSwitch = (workspaceId: string) => {
    setOpen(false);
    const url = new URL(window.location.href);

    if (workspaceId === defaultWorkspaceId) {
      // Default workspace - remove ?ws for cleaner URL
      url.searchParams.delete('ws');
    } else {
      // Non-default workspace - add ?ws param
      url.searchParams.set('ws', workspaceId);
    }

    window.location.href = url.toString();
  };

  // Navigate to workspace settings (preserve workspace context)
  const handleSettings = () => {
    if (onNavGuard?.('/dashboard/settings/workspace')) {
      setOpen(false);
      return;
    }
    appNavigate('/dashboard/settings/workspace');
    setOpen(false);
  };

  // Open invite members dialog (admin/owner only)
  const handleInviteMembers = () => {
    setOpen(false);
    setInviteDialogOpen(true);
  };

  // Handle successful invite
  const handleInviteSuccess = () => {
    refreshWorkspaces();
  };

  // Open create workspace modal
  const handleCreateWorkspace = () => {
    setOpen(false);
    setCreateDialogOpen(true);
  };

  // Handle workspace created - just refresh list, stay in current workspace
  const handleWorkspaceCreated = async () => {
    await refreshWorkspaces();
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {collapsed ? (
          <button
            className="w-9 h-9 mx-auto flex items-center justify-center rounded-md bg-foreground/[0.06] text-foreground dark:bg-white/[0.08] dark:text-white font-semibold text-sm hover:bg-accent/80 dark:hover:bg-white/[0.12] transition-colors -my-0.5"
          >
            {getInitial(displayWorkspace.name)}
          </button>
        ) : (
          <button
            className={cn(
              'w-full flex items-center gap-4 lg:gap-3 p-3 lg:p-1.5 rounded-xl lg:rounded-lg',
              'hover:bg-accent/50 transition-colors',
              'text-left'
            )}
          >
            {/* Workspace Avatar */}
            <div className="w-10 h-10 lg:w-8 lg:h-8 flex-shrink-0 flex items-center justify-center rounded-xl lg:rounded-md bg-foreground/[0.06] text-foreground dark:bg-white/[0.08] dark:text-white font-semibold text-base lg:text-sm -my-0.5">
              {getInitial(displayWorkspace.name)}
            </div>

            {/* Workspace Name */}
            <div className="flex-1 min-w-0">
              <p className="text-base lg:text-sm font-medium text-foreground truncate">
                {displayWorkspace.name}
              </p>
            </div>

            {/* Chevron */}
            <ChevronDown
              className={cn(
                'h-5 w-5 lg:h-4 lg:w-4 text-muted-foreground transition-transform',
                open && 'rotate-180'
              )}
            />
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent
        className="w-72 p-0"
        align={collapsed ? 'start' : 'start'}
        side={collapsed ? 'right' : 'bottom'}
        sideOffset={collapsed ? 8 : 4}
      >
        {/* Current Workspace Header */}
        <div className="p-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-foreground/[0.06] text-foreground dark:bg-white/[0.08] dark:text-white font-semibold text-sm">
              {getInitial(displayWorkspace.name)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {displayWorkspace.name}
              </p>
              {/* Show member count only for member workspaces */}
              {!currentWorkspace && displayWorkspace.memberCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {displayWorkspace.memberCount} member
                  {displayWorkspace.memberCount !== 1 ? 's' : ''}
                </p>
              )}
              {/* Show owner info for platform admin viewing non-member workspace */}
              {currentWorkspace && currentWorkspace.ownerEmail && (
                <p className="text-xs text-muted-foreground">
                  Owner: {currentWorkspace.ownerDisplayName || currentWorkspace.ownerEmail}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={handleSettings}
            >
              <Settings className="h-3.5 w-3.5 mr-1" />
              Settings
            </Button>
            {canInviteMembers && !isEmbedded && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-7 text-xs"
                onClick={handleInviteMembers}
              >
                <UserPlus className="h-3.5 w-3.5 mr-1" />
                Invite
              </Button>
            )}
          </div>

          {/* BYOC Connected or Credit Usage Bar */}
          {byocStatus?.connected ? (
            // BYOC Connected State
            <div className="mt-3 -mx-3 pt-3 px-2 border-t border-border">
              <div className="flex items-center gap-2 py-1.5 px-2">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                  <Zap className="h-3 w-3 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-xs text-foreground">
                  {getByocMessage(byocStatus.authMethod)}
                </p>
              </div>
            </div>
          ) : creditDetails && (
            // Credits Usage Bar
            <div className="mt-3 -mx-3 pt-3 px-2 border-t border-border">
              <button
                onClick={handleBillingClick}
                className="w-full text-left hover:bg-accent/50 py-1.5 px-2 transition-colors cursor-pointer rounded-md"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-muted-foreground">
                    {isFreeDaily ? "Today's free credits" : 'Credits remaining'}
                  </p>
                  {creditDetails.nextResetAt && !isOutOfCredits && (
                    <Hint text={`Resets ${formatResetDate(creditDetails.nextResetAt)}.`}>
                      <span className="text-[10px] text-muted-foreground/70 cursor-help">
                        Resets {formatResetDate(creditDetails.nextResetAt)}
                      </span>
                    </Hint>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden border border-border">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        isOutOfCredits ? 'bg-red-500' : 'bg-primary'
                      )}
                      style={{ width: `${isOutOfCredits ? 100 : creditPercentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {Math.max(0, Math.round(creditDetails.subscriptionBalance))} of{' '}
                    {Math.round(creditDetails.allocated)}
                    {isFreeDaily ? '' : ' monthly'}
                  </span>
                </div>

                {creditDetails.topupBalance > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    +{Math.round(creditDetails.topupBalance)} top-up credit
                    {Math.round(creditDetails.topupBalance) !== 1 ? 's' : ''}
                  </p>
                )}

                {isOutOfCredits && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-red-500">
                    <TriangleAlert className="h-3 w-3 flex-shrink-0" />
                    <p className="text-[11px] font-medium">
                      You're out of credits. Agent work is paused until you top up or your plan renews.
                    </p>
                  </div>
                )}
              </button>
              {/* BYOC CTA */}
              {byocEnabled && (
                <div className="mt-2 mx-1 p-2 rounded-md bg-muted border border-border">
                  <div className="flex items-center gap-2 text-[11px]">
                    <Sparkles className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-muted-foreground">Have a Claude subscription?</span>
                    <Link
                      to="/dashboard/settings/workspace?claude=reconnect"
                      className="text-primary hover:underline font-medium ml-auto"
                      onClick={(e) => {
                        if (onNavGuard?.('/dashboard/settings/workspace?claude=reconnect')) {
                          e.preventDefault();
                        }
                        setOpen(false);
                      }}
                    >
                      Connect
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-border" />

        {/* Current Workspace (if platform admin viewing non-member workspace) */}
        {currentWorkspace && (
          <div className="p-2">
            <p className="px-2 py-1 text-[11px] font-medium text-muted-foreground">
              Current workspace
            </p>
            <div className="space-y-0.5">
              <button
                key={currentWorkspace.id}
                onClick={() => handleSwitch(currentWorkspace.id)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-md',
                  'bg-accent/50',
                  'text-left'
                )}
              >
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md bg-foreground/[0.06] text-foreground dark:bg-white/[0.08] dark:text-white font-semibold text-xs">
                  {getInitial(currentWorkspace.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">
                    {currentWorkspace.name}
                  </p>
                </div>
                <Check className="h-3.5 w-3.5 text-foreground flex-shrink-0" />
              </button>
            </div>
          </div>
        )}

        {currentWorkspace && <div className="border-t border-border" />}

        {/* My Workspaces */}
        <div className="p-2">
          <p className="px-2 py-1 text-[11px] font-medium text-muted-foreground">
            {currentWorkspace ? 'My workspaces' : 'All workspaces'}
          </p>
          <div className="space-y-0.5">
            {(isEmbedded ? workspaces.filter((w) => w.id === displayWorkspace.id) : workspaces).map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => handleSwitch(workspace.id)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-md',
                  'hover:bg-accent transition-colors',
                  'text-left'
                )}
              >
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md bg-foreground/[0.06] text-foreground dark:bg-white/[0.08] dark:text-white font-semibold text-xs">
                  {getInitial(workspace.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">
                    {workspace.name}
                  </p>
                </div>
                {!currentWorkspace && workspace.id === displayWorkspace.id && (
                  <Check className="h-3.5 w-3.5 text-foreground flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Create New Workspace - hidden when createWorkspace feature is disabled or in embed mode */}
        {canCreateWorkspace && features.createWorkspace && !isEmbedded && (
          <>
            <div className="border-t border-border" />
            <div className="p-2">
              <button
                onClick={handleCreateWorkspace}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-md',
                  'hover:bg-accent transition-colors',
                  'text-left text-muted-foreground hover:text-foreground'
                )}
              >
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md border border-dashed border-muted-foreground/30">
                  <Plus className="h-3.5 w-3.5" />
                </div>
                <p className="text-xs font-medium">Create new workspace</p>
              </button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>

    <CreateWorkspaceDialog
      open={createDialogOpen}
      onOpenChange={setCreateDialogOpen}
      onSuccess={handleWorkspaceCreated}
    />

    {canInviteMembers && (
      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onSuccess={handleInviteSuccess}
        memberLimit={null}
        currentCount={displayWorkspace?.memberCount || 0}
      />
    )}
    </>
  );
}
